import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import {
  getUserApprovalFlows, getApprovalFlows, assignUsers, removeUser,
} from '../../../services/approvalFlowService';
import { useAuth } from '../../../context/AuthContext';
import { FLOW_TYPES } from '../../approval-flows/config/flowTypes';
import DataTable from '../../../components/ui/DataTable';
import GenericFormDrawer from '../../../components/GenericFormDrawer';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import OptionsMenu from '../../../components/ui/OptionsMenu';

// Sekme akış tipi bazlı SLOT listesidir: kiracının tanımlı her akış tipi için bir
// satır. Backend ilişkiyi (UserId + FlowType) benzersiz tuttuğu için bir çalışanın
// her tipte en çok bir akışı olabilir — tablo bu değişmezi birebir yansıtır.
//
// Atanmamış satır "kayıt yok" DEĞİLDİR: ApprovalHelper.DetermineApproval o durumda
// talebi pozisyon hiyerarşisindeki amire, amir yoksa İK'ya yönlendirir. Satır bu
// gerçek rotayı yazar, böylece ekran hiçbir zaman ölü bir boş liste olmaz.
const FLOW_PAGE_SIZE = 200;

export default function EmployeeApprovalFlowsTab({ employeeId }) {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const hasWrite = canWrite('EM');

  const [assigned, setAssigned] = useState([]);
  const [flows, setFlows] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [drawer, setDrawer] = useState({ open: false, flowType: null, currentFlowId: null });
  const [revoke, setRevoke] = useState({ open: false, approvalFlowId: null, flowName: '' });
  const [revoking, setRevoking] = useState(false);

  const fetchAll = useCallback(async () => {
    setFetching(true);
    try {
      const [assignedRes, flowsRes] = await Promise.all([
        getUserApprovalFlows(employeeId),
        getApprovalFlows({ pageSize: FLOW_PAGE_SIZE }),
      ]);
      setAssigned(assignedRes.data?.data || []);
      const page = flowsRes.data?.data;
      setFlows(Array.isArray(page) ? page : (page?.data || []));
    } catch {
      // Hata mesajı Axios interceptor'ında gösterilir.
    }
    setFetching(false);
  }, [employeeId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const rows = useMemo(() => FLOW_TYPES.map((type) => {
    const relation = assigned.find((a) => a.flowType === type.id) || null;
    const flow = relation ? flows.find((f) => f.id === relation.approvalFlowId) : null;
    return {
      id: type.id,
      flowType: type.id,
      flowTypeLabel: t(type.labelKey),
      approvalFlowId: relation?.approvalFlowId ?? null,
      flowName: relation?.flowName ?? null,
      // Akışın kaynağı (spec 2026-11-01 §4.6): kişiye özel bağlama ya da canlı kural (departman / ekip …).
      source: relation ? (relation.source || 'person') : null,
      sourceName: relation?.sourceName ?? null,
      stepCount: flow?.stepCount ?? null,
      // Kiracıda bu tipte hiç akış tanımlı değilse atanacak bir şey yoktur.
      hasCandidates: flows.some((f) => f.flowType === type.id),
    };
  }), [assigned, flows, t]);

  const openAssignDrawer = useCallback((row) => {
    setDrawer({ open: true, flowType: row.flowType, currentFlowId: row.approvalFlowId });
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawer({ open: false, flowType: null, currentFlowId: null });
  }, []);

  const handleAssign = useCallback(async (formData) => {
    if (!formData.approvalFlowId) return false;
    try {
      // Backend aynı tipteki önceki ilişkiyi kendisi siler; "değiştir" için ayrı
      // bir geri alma çağrısı gerekmez.
      await assignUsers(formData.approvalFlowId, { employeeIds: [employeeId] });
      await fetchAll();
      closeDrawer();
      return true;
    } catch {
      return false;
    }
  }, [employeeId, fetchAll, closeDrawer]);

  const handleRevokeConfirm = useCallback(async () => {
    setRevoking(true);
    try {
      await removeUser(revoke.approvalFlowId, employeeId);
      await fetchAll();
    } catch {
      // interceptor
    }
    setRevoking(false);
    setRevoke({ open: false, approvalFlowId: null, flowName: '' });
  }, [revoke.approvalFlowId, employeeId, fetchAll]);

  const actionOptions = useMemo(() => [
    {
      type: 'edit',
      label: t('approvalFlow.assignFlow'),
      condition: (row) => row.hasCandidates && !row.approvalFlowId,
      onClick: openAssignDrawer,
    },
    {
      type: 'move_stage',
      label: t('approvalFlow.changeFlow'),
      condition: (row) => row.hasCandidates && !!row.approvalFlowId,
      onClick: openAssignDrawer,
    },
    {
      type: 'delete',
      label: t('approvalFlow.revokeAssignment'),
      // Yalnız kişiye özel atama geri alınır; kural kaynaklı akış akışın "Kurallar" sekmesinden yönetilir.
      condition: (row) => !!row.approvalFlowId && row.source === 'person',
      onClick: (row) => setRevoke({
        open: true, approvalFlowId: row.approvalFlowId, flowName: row.flowName || '',
      }),
    },
  ], [t, openAssignDrawer]);

  const columns = useMemo(() => {
    const base = [
      { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
      { name: 'flowTypeLabel', label: t('approvalFlow.flowType'), options: { filter: false, sort: false } },
      {
        name: 'flowName',
        label: t('approvalFlow.assignedFlow'),
        options: {
          filter: false, sort: false,
          customBodyRender: (value) => (value
            ? <AssignedName>{value}</AssignedName>
            : <Muted>{t('approvalFlow.defaultManagerChain')}</Muted>),
        },
      },
      {
        name: 'source',
        label: t('approvalFlow.sourceLabel'),
        options: {
          filter: false, sort: false,
          customBodyRender: (value, tableMeta) => {
            const row = rows[tableMeta.rowIndex];
            if (!value || !row) return <Muted>-</Muted>;
            const label = t(`approvalFlow.source.${value}`);
            return row.sourceName ? `${label}: ${row.sourceName}` : label;
          },
        },
      },
      {
        name: 'stepCount',
        label: t('approvalFlow.stepCount'),
        options: {
          filter: false, sort: false,
          customBodyRender: (value) => (value ?? '-'),
        },
      },
    ];

    if (!hasWrite) return base;

    return [...base, {
      name: 'actions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = rows[tableMeta.rowIndex];
          if (!row) return null;
          // Görünür aksiyonu olmayan satırda menü düğmesi hiç çizilmez — boş bir
          // açılır liste kullanıcıya tıklanabilir ama işlevsiz bir düğme gösterir.
          const hasVisible = actionOptions.some((a) => a.condition(row));
          return hasVisible ? <OptionsMenu actions={actionOptions} row={row} /> : null;
        },
      },
    }];
  }, [t, hasWrite, rows, actionOptions]);

  const drawerSchema = useMemo(() => [{
    name: 'approvalFlowId',
    label: t('approvalFlow.name'),
    type: 'asyncSelect',
    grid: 12,
    required: true,
    onSearch: async (query) => {
      const params = { pageSize: FLOW_PAGE_SIZE, 'flowType[eq]': drawer.flowType };
      if (query) params['name[like]'] = query;
      try {
        const res = await getApprovalFlows(params);
        const page = res.data?.data;
        const items = Array.isArray(page) ? page : (page?.data || []);
        return items.map((f) => ({ id: f.id, name: f.name }));
      } catch {
        return [];
      }
    },
  }], [t, drawer.flowType]);

  return (
    <Container>
      <GenericFormDrawer
        open={drawer.open}
        onClose={closeDrawer}
        title={drawer.currentFlowId ? t('approvalFlow.changeFlow') : t('approvalFlow.assignFlow')}
        schema={drawerSchema}
        initialValues={drawer.currentFlowId ? { approvalFlowId: drawer.currentFlowId } : undefined}
        onSave={handleAssign}
      />

      <ConfirmDialog
        isOpen={revoke.open}
        title={t('approvalFlow.revokeAssignment')}
        message={t('approvalFlow.revokeAssignmentMessage', { name: revoke.flowName })}
        onConfirm={handleRevokeConfirm}
        onCancel={() => setRevoke({ open: false, approvalFlowId: null, flowName: '' })}
        loading={revoking}
      />

      <DataTable
        title={t('employee.approvalFlows')}
        fetching={fetching}
        columns={columns}
        data={rows}
        totalRowCount={rows.length}
        pageIndex={0}
        // Sabit ve kısa liste: sayfa boyutu mui-datatables'ın rowsPerPageOptions
        // değerlerinden biri olmalı, aksi halde tablo konsola uyarı basar.
        pageSize={10}
        serverSide={false}
        disableFilters
      />

      <FooterNote>{t('approvalFlow.defaultRoutingHint')}</FooterNote>
    </Container>
  );
}

const Container = styled.div``;

const AssignedName = styled.span`
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Muted = styled.span`
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const FooterNote = styled.p`
  margin-top: 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.dark.little};
`;
